import React from 'react';
import { useNavigate } from "react-router";
import {
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Stack,
    Box,
    Typography,
    Badge,
    Tooltip,
    useTheme,
    IconButton,
    Menu,
    MenuItem,
} from '@mui/material';
import {
    Event as EventIcon,
    Task as TaskIcon,
    Warning as WarningIcon,
    Today as TodayIcon,
    Schedule as ScheduleIcon,
    CheckCircle as CheckCircleIcon,
    NotificationsActive as AlertIcon,
    AccessTime as PastIcon,
    ArrowForward as FutureIcon,
    Circle as CircleIcon,
    RadioButtonChecked as DotIcon,
    MoreVert as MoreVertIcon,
    Edit as EditIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    Delete as DeleteIcon
} from '@mui/icons-material';
import LinkIcon from "@mui/icons-material/Link";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import ImportContactsIcon from '@mui/icons-material/ImportContacts';

import { format, isToday, isPast, isFuture } from 'date-fns';
import { formatStatusDate } from '../../functions/dateFormatter';

import {
    DiaryItem,
    DiaryItemStatus,
    DiaryItemType,
    DiaryCategory,
    DiaryCreatedSource,
} from "../../models/diaryTypes";

import { getDiaryLinkRoute } from "./link-to-helper";

import DiaryGroupedList from "./DiaryGroupedList";

import { useMediaQuery } from "@mui/material";


// Status calculation
type EntryStatus =
    | 'overdue-task'
    | 'task-due-today'
    | 'past-event'
    | 'event-today'
    | 'future-event'
    | 'completed-task'
    | 'cancelled-task'
    | 'future-task'
    | 'cancelled-event';

type DiaryView = 'grouped' | 'list' | 'compact';
    
const getEntryStatus = (entry: DiaryItem): EntryStatus => {
    
    const now = new Date();

    // Handle cancelled items first
    if (entry.status === DiaryItemStatus.Cancelled) {
        return entry.type === DiaryItemType.Task ? 'cancelled-task' : 'cancelled-event';
    }

    if (entry.type === DiaryItemType.Task) {
        if (entry.status === DiaryItemStatus.Completed) return 'completed-task';

        if (!entry.dueUtc) return 'future-task';

        const dueDate = new Date(entry.dueUtc);

        if (isPast(dueDate) && !isToday(dueDate)) {
            return 'overdue-task';
        }

        if (isToday(dueDate)) {
            return 'task-due-today';
        }

        return 'future-task';
    }

    // For Events
    if (!entry.endUtc) return 'future-event';

    const endDate = new Date(entry.endUtc);

    if (isPast(endDate) && !isToday(endDate)) {
        return 'past-event';
    }

    if (isToday(endDate) || (entry.startUtc && isToday(new Date(entry.startUtc)))) {
        return 'event-today';
    }

    return 'future-event';
};

// Chip colors
type ChipColor = 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const statusColors: Record<EntryStatus, ChipColor> = {
    'overdue-task': 'error',
    'task-due-today': 'warning',
    'past-event': 'secondary',
    'event-today': 'primary',
    'future-event': 'info',
    'completed-task': 'success',
    'cancelled-task': 'default' as any, // Using default styling for cancelled
    'future-task': 'secondary',
    'cancelled-event': 'default' as any
};

// Color mapping for borders
const statusBorderColors: Record<EntryStatus, string> = {
    'overdue-task': 'error.main',
    'task-due-today': 'warning.main',
    'past-event': 'grey.400',
    'event-today': 'primary.main',
    'future-event': 'info.main',
    'completed-task': 'success.main',
    'cancelled-task': 'grey.400',
    'future-task': 'secondary.main',
    'cancelled-event': 'grey.400'
};

// Label mapping
const statusLabels: Record<EntryStatus, string> = {
    'overdue-task': 'Overdue',
    'task-due-today': 'Due Today',
    'past-event': 'Past Event',
    'event-today': 'Today',
    'future-event': 'Upcoming',
    'completed-task': 'Completed',
    'cancelled-task': 'Cancelled',
    'future-task': 'Future',
    'cancelled-event': 'Cancelled'
};

// Icon mapping
const StatusIcons: Record<EntryStatus, React.ReactElement> = {
    'overdue-task': <WarningIcon />,
    'task-due-today': <TodayIcon />,
    'past-event': <PastIcon />,
    'event-today': <AlertIcon />,
    'future-event': <FutureIcon />,
    'completed-task': <CheckCircleIcon />,
    'cancelled-task': <CloseIcon />,
    'future-task': <ScheduleIcon />,
    'cancelled-event': <CloseIcon />
};

// Priority icons
const PriorityIcon = ({ priority }: { priority?: 'low' | 'medium' | 'high' }) => {
    switch (priority) {
        case 'high':
            return <DotIcon color="error" fontSize="small" />;
        case 'medium':
            return <DotIcon color="warning" fontSize="small" />;
        case 'low':
            return <DotIcon color="success" fontSize="small" />;
        default:
            return <CircleIcon fontSize="small" color="disabled" />;
    }
};

// Action types
export type EntryAction = 'view' | 'edit' | 'complete' | 'cancel' | 'delete';

interface EntryActionsProps {
    entry: DiaryItem;
    onAction: (action: EntryAction, entry: DiaryItem | number) => void;
    disabled?: boolean;
}

const EntryActions: React.FC<EntryActionsProps> = ({ entry, onAction, disabled = false }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event?: React.MouseEvent) => {
        event?.stopPropagation();
        setAnchorEl(null);
    };

    const handleAction = (action: EntryAction) => {
        handleClose();
        if (action === 'edit' || action === 'view') {
            onAction(action, entry);
        } else {
            onAction(action, entry.id);
        }
    };

    const status = getEntryStatus(entry);
    const isTask = entry.type === DiaryItemType.Task;
    const isEvent = entry.type === DiaryItemType.Event;
    const isCompletedOrCancelled =
        status === 'completed-task' ||
        status === 'cancelled-task' ||
        status === 'cancelled-event';
    const isParent = entry.parentDiaryItemId === null && entry.links?.length > 0;

    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                disabled={disabled}
                sx={{
                    opacity: { xs: 1, sm: 0.7 },
                    '&:hover': { opacity: 1 },
                    p: 0.75
                }}
            >
                <MoreVertIcon fontSize="small" />
            </IconButton>

            <Menu
                keepMounted
                anchorEl={anchorEl}
                open={open}
                onClose={() => handleClose()}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            minWidth: 180
                        }
                    }
                }}
            >
                {status !== 'cancelled-event' && entry.status !== DiaryItemStatus.Completed && (
                    <MenuItem onClick={() => handleAction('edit')} sx={{ minHeight: 44 }}>
                        <ListItemIcon>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Edit</ListItemText>
                    </MenuItem>
                )}

                {entry.status === DiaryItemStatus.Completed && (
                    <MenuItem onClick={() => handleAction('view')} sx={{ minHeight: 44 }}>
                        <ListItemIcon>
                            <ImportContactsIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>View</ListItemText>
                    </MenuItem>
                )}

                {isTask &&
                    entry.status !== DiaryItemStatus.Completed &&
                    entry.status !== DiaryItemStatus.Cancelled &&
                    !isParent && (
                        <MenuItem onClick={() => handleAction('complete')} sx={{ minHeight: 44 }}>
                            <ListItemIcon>
                                <CheckIcon fontSize="small" color="success" />
                            </ListItemIcon>
                            <ListItemText>Mark Complete</ListItemText>
                        </MenuItem>
                    )}

                {!isCompletedOrCancelled && (isTask || (isEvent && status === 'future-event')) && (
                    <MenuItem onClick={() => handleAction('cancel')} sx={{ minHeight: 44 }}>
                        <ListItemIcon>
                            <CloseIcon fontSize="small" color="error" />
                        </ListItemIcon>
                        <ListItemText>Cancel</ListItemText>
                    </MenuItem>
                )}

                {(status === 'cancelled-task' ||
                    status === 'cancelled-event' ||
                    status === 'past-event') && (
                        <MenuItem onClick={() => handleAction('delete')} sx={{ minHeight: 44 }}>
                            <ListItemIcon>
                                <DeleteIcon fontSize="small" color="error" />
                            </ListItemIcon>
                            <ListItemText>Delete</ListItemText>
                        </MenuItem>
                    )}
            </Menu>
        </>
    );
};

// Enhanced status indicator
interface StatusIndicatorProps {
    status: EntryStatus;
    showLabel?: boolean;
    size?: 'small' | 'medium';
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
    status,
    showLabel = false,
    size = 'medium'
}) => {
    const theme = useTheme();

    const statusConfigs: Record<EntryStatus, {
        icon: React.ReactElement;
        color: string;
        label: string;
        tooltip: string;
    }> = {
        'overdue-task': {
            icon: <WarningIcon fontSize={size} />,
            color: theme.palette.error.main,
            label: 'Overdue Task',
            tooltip: 'This task is past its due date'
        },
        'task-due-today': {
            icon: <TodayIcon fontSize={size} />,
            color: theme.palette.warning.main,
            label: 'Due Today',
            tooltip: 'This task is due today'
        },
        'past-event': {
            icon: <PastIcon fontSize={size} />,
            color: theme.palette.grey[500],
            label: 'Past Event',
            tooltip: 'This event has ended'
        },
        'event-today': {
            icon: <AlertIcon fontSize={size} />,
            color: theme.palette.primary.main,
            label: 'Event Today',
            tooltip: 'This event is happening today'
        },
        'future-event': {
            icon: <FutureIcon fontSize={size} />,
            color: theme.palette.info.main,
            label: 'Future Event',
            tooltip: 'This event is scheduled for the future'
        },
        'completed-task': {
            icon: <CheckCircleIcon fontSize={size} />,
            color: theme.palette.success.main,
            label: 'Completed',
            tooltip: 'This task is completed'
        },
        'cancelled-task': {
            icon: <CloseIcon fontSize={size} />,
            color: theme.palette.grey[500],
            label: 'Cancelled',
            tooltip: 'This task has been cancelled'
        },
        'future-task': {
            icon: <ScheduleIcon fontSize={size} />,
            color: theme.palette.secondary.main,
            label: 'Future Task',
            tooltip: 'This task is scheduled for the future'
        },
        'cancelled-event': {
            icon: <CloseIcon fontSize={size} />,
            color: theme.palette.grey[500],
            label: 'Cancelled',
            tooltip: 'This event has been cancelled'
        }
    };

    const config = statusConfigs[status];

    return (
        <Tooltip title={config.tooltip}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box sx={{ color: config.color }}>
                    {config.icon}
                </Box>
                {showLabel && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: config.color,
                            fontSize: size === 'small' ? '0.75rem' : '0.875rem'
                        }}
                    >
                        {config.label}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

// Custom Chip for cancelled and past events
const CustomChip = React.forwardRef<HTMLDivElement,
    React.ComponentProps<typeof Chip> & {
        customColor?: 'grey' | 'error' | 'warning' | 'success' | 'info' | 'primary'
    }>(({ customColor = 'grey', ...props }, ref) => {
        const theme = useTheme();

        const colorMap = {
            grey: {
                border: theme.palette.grey[400],
                text: theme.palette.grey[600],
                bg: theme.palette.grey[50],
                icon: theme.palette.grey[500]
            },
            error: {
                border: theme.palette.error.main,
                text: theme.palette.error.dark,
                bg: theme.palette.error.light,
                icon: theme.palette.error.main
            },
            warning: {
                border: theme.palette.warning.main,
                text: theme.palette.warning.dark,
                bg: theme.palette.warning.light,
                icon: theme.palette.warning.main
            },
            success: {
                border: theme.palette.success.main,
                text: theme.palette.success.dark,
                bg: theme.palette.success.light,
                icon: theme.palette.success.main
            },
            info: {
                border: theme.palette.info.main,
                text: theme.palette.info.dark,
                bg: theme.palette.info.light,
                icon: theme.palette.info.main
            },
            primary: {
                border: theme.palette.primary.main,
                text: theme.palette.primary.dark,
                bg: theme.palette.primary.light,
                icon: theme.palette.primary.main
            }
        };

        const colors = colorMap[customColor];

        return (
            <Chip
                ref={ref}
                {...props}
                sx={{
                    borderColor: colors.border,
                    color: colors.text,
                    bgcolor: colors.bg + '20', // Adding transparency
                    '& .MuiChip-icon': {
                        color: colors.icon,
                    },
                    ...props.sx
                }}
            />
        );
    });

CustomChip.displayName = 'CustomChip';

function ChildIndicator({ item, onOpenParent }: { item: DiaryItem; onOpenParent: (entry: DiaryItem) => void; }) {
    const navigate = useNavigate();

    if (!item.parentDiaryItemId) return null;

    if (item.createdSource === DiaryCreatedSource.System) {
        return (
            <Chip
                size="small"
                icon={<AutorenewIcon fontSize="small" />}
                label="Series"
                variant="outlined"
                /*onClick={() => navigate(`/diary/${item.parentDiaryItemId}`)}*/ 
                onClick={(e) => {
                    e.stopPropagation();
                    onOpenParent(item);
                }}
                sx={{ ml: 1 }}
            />
        );
    }

    return (
        <Chip
            size="small"
            icon={<LinkIcon fontSize="small" />}
            label="Linked"
            variant="outlined"
            onClick={(e) => {
                e.stopPropagation();
                onOpenParent(item);
            }}
            sx={{ ml: 1 }}
        />
    );
}
// Individual entry component
interface DiaryItemItemProps {
    entry: DiaryItem;
    onClick?: (entry: DiaryItem) => void;
    onAction: (action: EntryAction, entry: DiaryItem | number) => void;
    onParentEdit: (entry: DiaryItem) => void;
    showActions?: boolean;
}

const DiaryItemItem: React.FC<DiaryItemItemProps> = ({
    entry,
    onClick,
    onAction,
    onParentEdit,
    showActions = true
}) => {
    const status = getEntryStatus(entry);
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const isDisabledText =
        status === "completed-task" ||
        status === "cancelled-task" ||
        status === "cancelled-event";

    const getDateTimeText = (): string => {
        var displayDate = 'No date specified';

        if (entry.type === DiaryItemType.Task) {
            if (!entry.dueUtc) return 'No due date';
            const dueDate = new Date(entry.dueUtc);
            displayDate = formatStatusDate(entry.dueUtcStr);
            //return `Due: ${format(dueDate, 'MMM d, yyyy h:mm a')}`;
        } else {
            if (!entry.endUtc) return 'No end date';
            const endDate = new Date(entry.endUtc);
            displayDate = formatStatusDate(entry.endUtcStr);
            //return `Ends: ${format(endDate, 'MMM d, yyyy h:mm a')}`;
        }
        return displayDate;
    };

    const borderColor = statusBorderColors[status];

    // Render appropriate chip based on status
    const renderStatusChip = () => {
        
        const chipProps = {
            label: statusLabels[status],
            size: 'small' as const,
            variant: 'outlined' as const,
            icon: StatusIcons[status]
        };

        if (status === 'past-event' || status === 'cancelled-task' || status === 'cancelled-event') {
            return (
                <CustomChip
                    {...chipProps}
                    customColor="grey"
                />
            );
        }

        if (status === 'overdue-task') {
            return (
                <CustomChip
                    {...chipProps}
                    customColor="error"
                />
            );
        }

        if (status === 'task-due-today') {
            return (
                <CustomChip
                    {...chipProps}
                    customColor="warning"
                />
            );
        }

        if (status === 'completed-task') {
            return (
                <CustomChip
                    {...chipProps}
                    customColor="success"
                />
            );
        }

        // For other statuses, use regular chip with color prop
        return (
            <Chip
                {...chipProps}
                color={statusColors[status]}
            />
        );
    };

    return (
        <ListItem
            onClick={() => onClick?.(entry)}
            sx={{
                borderLeft: "4px solid",
                borderLeftColor: borderColor,
                mb: 1,
                bgcolor: "background.paper",
                borderRadius: 1,
                cursor: onClick ? "pointer" : "default",
                position: "relative",
                display: "block",
                px: 1.5,
                py: 1.25,
                transition: "background-color 0.2s ease",
                "&:hover": {
                    bgcolor: onClick ? "action.hover" : "background.paper",
                    "& .entry-actions": {
                        opacity: 1,
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    minWidth: 0,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "space-between",
                        alignItems: { xs: "stretch", sm: "flex-start" },
                        gap: 1,
                        minWidth: 0,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 1,
                            minWidth: 0,
                            flex: 1,
                            alignItems: "flex-start",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "row",
                                gap: 0.5,
                                pt: 0.25,
                                flexShrink: 0,
                            }}
                        >
                            <StatusIndicator status={status} />
                            {entry.type === DiaryItemType.Event ? (
                                <EventIcon color="action" fontSize="small" />
                            ) : (
                                <TaskIcon color="action" fontSize="small" />
                            )}
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        textDecoration:
                                            status === "completed-task"
                                                ? "line-through"
                                                : "none",
                                        color: isDisabledText
                                            ? "text.disabled"
                                            : "text.primary",
                                        minWidth: 0,
                                        whiteSpace: { xs: "normal", sm: "nowrap" },
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontWeight: 500,
                                    }}
                                >
                                    {entry.title}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 1,
                                    flexWrap: "wrap",
                                    minWidth: 0,
                                }}
                            >
                                <ChildIndicator
                                    item={entry}
                                    onOpenParent={() => onParentEdit?.(entry)}
                                />

                                <Chip
                                    size="small"
                                    label={DiaryCategory[entry.category]}
                                />

                                {!isMobile &&
                                    renderStatusChip()
                                }
                            </Box>
                            {entry.description && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        mt: 0.5,
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {entry.description}
                                </Typography>
                            )}

                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                    display: "block",
                                    mt: 0.5,
                                }}
                            >
                                {getDateTimeText()}
                            </Typography>
                        </Box>
                    </Box>

                    {showActions && (
                        <Box
                            className="entry-actions"
                            sx={{
                                display: "flex",
                                justifyContent: {
                                    xs: "flex-end",
                                    sm: "flex-end",
                                },
                                width: { xs: "100%", sm: "auto" },
                                flexShrink: 0,
                                opacity: { xs: 1, sm: 0.7 },
                                transition: "opacity 0.2s ease",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <EntryActions
                                entry={entry}
                                onAction={onAction}
                            />
                        </Box>
                    )}
                </Box>

                {entry.links?.length > 0 && (
                    <Stack
                        direction="row"
                        spacing={1}
                        sx={{
                            flexWrap: "wrap",
                            rowGap: 1,
                            mt: 0.25,
                        }}
                    >
                        {entry.links.map((l, idx) => {
                            const route = getDiaryLinkRoute(l);
                            const label =
                                l.label && l.label.trim()
                                    ? l.label
                                    : `Artwork #${l.entityId}`;

                            return (
                                <Chip
                                    key={`${l.entityType}-${l.entityId}-${idx}`}
                                    size="small"
                                    label={label}
                                    clickable={!!route}
                                    onClick={
                                        route
                                            ? (e) => {
                                                e.stopPropagation();
                                                navigate(route);
                                            }
                                            : undefined
                                    }
                                    variant="outlined"
                                />
                            );
                        })}
                    </Stack>
                )}
            </Box>
        </ListItem>
    );
};

// Grouped list component
interface GroupedDiaryEntriesProps {
    entries: DiaryItem[];
    onEntryClick?: (entry: DiaryItem) => void;
    onAction: (action: EntryAction, entry: DiaryItem | number) => void;
    onParentEdit: (entry: DiaryItem) => void;
    showActions?: boolean;
}

const GroupedDiaryEntries: React.FC<GroupedDiaryEntriesProps> = ({
    entries,
    onEntryClick,
    onAction,
    onParentEdit,
    showActions = true
}) => {
    const groupedEntries = React.useMemo(() => {
        const groups: Record<EntryStatus, DiaryItem[]> = {
            'overdue-task': [],
            'task-due-today': [],
            'past-event': [],
            'event-today': [],
            'future-event': [],
            'completed-task': [],
            'cancelled-task': [],
            'future-task': [],
            'cancelled-event': []
        };

        entries.forEach(entry => {
            const status = getEntryStatus(entry);
            groups[status].push(entry);
        });

        return groups;
    }, [entries]);

    // Order of display groups
    const displayOrder: EntryStatus[] = [
        'overdue-task',
        'task-due-today',
        'event-today',
        'future-event',
        'future-task',
        'past-event',
        'completed-task',
        'cancelled-task',
        'cancelled-event'
    ];

    return (
        <Box>
            {displayOrder.map(status => {
                const entriesInGroup = groupedEntries[status];
                if (entriesInGroup.length === 0) return null;

                return (
                    <Box key={status} sx={{ mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                            <StatusIndicator status={status} showLabel />
                            <Typography variant="h6" sx={{ flex: 1 }}>
                                {statusLabels[status]}
                                <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                                    ({entriesInGroup.length})
                                </Typography>
                            </Typography>
                        </Box>
                        <List>
                            {entriesInGroup.map(entry => (
                                <DiaryItemItem
                                    key={entry.id}
                                    entry={entry}
                                    onClick={onEntryClick}
                                    onAction={onAction}
                                    onParentEdit={onParentEdit}
                                    showActions={showActions}
                                />
                            ))}
                        </List>
                    </Box>
                );
            })}
        </Box>
    );
};

// Compact view for dashboard with quick actions
interface DiaryItemCompactCardProps {
    entry: DiaryItem;
    onClick?: (entry: DiaryItem) => void;
    onAction: (action: EntryAction, entry: DiaryItem | number) => void;
    showActions?: boolean;
}

const DiaryItemCompactCard: React.FC<DiaryItemCompactCardProps> = ({
    entry,
    onClick,
    onAction,
    showActions = true
}) => {
    const status = getEntryStatus(entry);
    const borderColor = statusBorderColors[status];

    return (
        <Box
            onClick={() => onClick?.(entry)}
            sx={{
                p: 1.5,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                mb: 1,
                bgcolor: 'background.paper',
                borderLeft: `3px solid`,
                borderLeftColor: borderColor,
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': {
                    bgcolor: onClick ? 'action.hover' : 'background.paper'
                },
                position: 'relative'
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Box sx={{ width: 24 }}>
                    <StatusIndicator status={status} size="small" />
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 'medium',
                        textDecoration: status === 'completed-task' ? 'line-through' : 'none',
                        flex: 1,
                        color: (status === 'completed-task' || status === 'cancelled-task' || status === 'cancelled-event')
                            ? 'text.disabled'
                            : 'text.primary'
                    }}
                >
                    {entry.title}
                </Typography>

                {showActions && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {/* Quick complete button for tasks */}
                        {entry.type === DiaryItemType.Task && entry.status !== DiaryItemStatus.Completed && entry.status !== DiaryItemStatus.Cancelled && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction('complete', entry.id);
                                }}
                                sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'success.main' } }}
                            >
                                <CheckIcon fontSize="small" />
                            </IconButton>
                        )}

                        {/* Edit button */}
                        {status !== 'cancelled-event' && (
                            <IconButton
                                size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onAction('edit', entry);
                                }}
                                sx={{ opacity: 0.7, '&:hover': { opacity: 1, color: 'primary.main' } }}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                )}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap>
                {entry.type} • {statusLabels[status]}
            </Typography>
        </Box>
    );
};

// Main diary list component
interface DiaryEntriesListProps {
    entries: DiaryItem[];
    onEntryClick: (entry: DiaryItem) => void;
    onAction: (action: EntryAction, entry: DiaryItem | number) => void;
    onParentEdit: (entry: DiaryItem) => void;
    mode?: DiaryView;
    showActions?: boolean;
}

const DiaryEntriesList: React.FC<DiaryEntriesListProps> = ({
    entries,
    onEntryClick,
    onAction,
    onParentEdit,
    mode = 'grouped',
    showActions = true
}) => {
    // Summary badges at top
    const summaryBadges = React.useMemo(() => {
        return Object.entries(statusLabels).map(([statusKey, label]) => {
            const status = statusKey as EntryStatus;
            const count = entries.filter(e => getEntryStatus(e) === status).length;
            if (count === 0) return null;

            const chipProps = {
                icon: StatusIcons[status],
                label: label,
                size: 'small' as const,
                variant: 'outlined' as const
            };

            // Use CustomChip for certain statuses
            if (status === 'past-event' || status === 'cancelled-task' || status === 'cancelled-event') {
                return (
                    <Badge
                        key={status}
                        badgeContent={count}
                        color="default"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.75rem',
                                height: 20,
                                minWidth: 20
                            }
                        }}
                    >
                        <CustomChip
                            {...chipProps}
                            customColor="grey"
                        />
                    </Badge>
                );
            }

            if (status === 'overdue-task') {
                return (
                    <Badge
                        key={status}
                        badgeContent={count}
                        color="error"
                        sx={{
                            '& .MuiBadge-badge': {
                                fontSize: '0.75rem',
                                height: 20,
                                minWidth: 20
                            }
                        }}
                    >
                        <CustomChip
                            {...chipProps}
                            customColor="error"
                        />
                    </Badge>
                );
            }

            return (
                <Badge
                    key={status}
                    badgeContent={count}
                    color={statusColors[status]}
                    sx={{
                        '& .MuiBadge-badge': {
                            fontSize: '0.75rem',
                            height: 20,
                            minWidth: 20
                        }
                    }}
                >
                    <Chip
                        {...chipProps}
                        color={statusColors[status]}
                    />
                </Badge>
            );
        }).filter(Boolean) as React.ReactElement[];
    }, [entries]);

    return (
        <Box>
            {/* Summary badges */}
            {summaryBadges.length > 0 && (
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 3,
                    flexWrap: 'wrap',
                    justifyContent: 'center'
                }}>
                    {summaryBadges}
                </Box>
            )}

            {/* Content based on mode */}
            {mode === 'grouped' ? (
                <GroupedDiaryEntries
                    entries={entries}
                    onEntryClick={onEntryClick}
                    onAction={onAction}
                    onParentEdit={onParentEdit }
                    showActions={showActions}
                />
            ) : mode === 'compact' ? (
                <Box>
                    {entries.map(entry => (
                        <DiaryItemCompactCard
                            key={entry.id}
                            entry={entry}
                            onClick={onEntryClick}
                            onAction={onAction}
                            showActions={showActions}
                        />
                    ))}
                </Box>
            ) : (
                <DiaryGroupedList
                    items={entries}
                    onOpenItem={onEntryClick}
                            renderIndicators={(item) => <ChildIndicator item={item} onOpenParent={() => onParentEdit?.(item)} />}

                />

            )}
        </Box>
    );
};

// Export everything
export {
    DiaryEntriesList,
    DiaryItemItem,
    GroupedDiaryEntries,
    DiaryItemCompactCard,
    EntryActions,
    getEntryStatus,
    statusLabels,
    StatusIndicator
};
export type { EntryStatus, DiaryEntriesListProps, DiaryView };